import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";
import { homeApi } from "./home.api";
import { HomeStats, ChartDataPoint, ChartTimeRange, HomeState } from "./home.entity";
import { AppointmentJoin } from "@/repositories/appointments.repository";

const initialState: HomeState = {
  stats: null,
  chartData: [],
  chartTimeRange: "30d",
  upcomingAppointments: [],
  loading: false,
  error: null,
  lastUpdated: null,
};

export const fetchHomeData = createAsyncThunk(
  "home/fetchData",
  async (timeRange: ChartTimeRange | undefined, { getState, rejectWithValue }) => {
    try {
      const range = timeRange ?? (getState() as { home: HomeState }).home.chartTimeRange;
      const [stats, chartData, upcomingAppointments] = await Promise.all([
        homeApi.getHomeStats(range),
        homeApi.getChartData(range),
        homeApi.getUpcomingAppointments(),
      ]);
      return { stats, chartData, upcomingAppointments };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : "Failed to fetch home data");
    }
  }
);

export const fetchStats = createAsyncThunk(
  "home/fetchStats",
  async (timeRange: ChartTimeRange | undefined, { getState, rejectWithValue }) => {
    try {
      const range = timeRange ?? (getState() as { home: HomeState }).home.chartTimeRange;
      return await homeApi.getHomeStats(range);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : "Failed to fetch stats");
    }
  }
);

export const fetchChartData = createAsyncThunk(
  "home/fetchChartData",
  async (timeRange: ChartTimeRange, { rejectWithValue }) => {
    try {
      return await homeApi.getChartData(timeRange);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : "Failed to fetch chart data");
    }
  }
);

export const fetchUpcomingAppointments = createAsyncThunk(
  "home/fetchUpcomingAppointments",
  async (_, { rejectWithValue }) => {
    try {
      return await homeApi.getUpcomingAppointments();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : "Failed to fetch upcoming appointments");
    }
  }
);

const homeSlice = createSlice({
  name: "home",
  initialState,
  reducers: {
    setChartTimeRange: (state, action: PayloadAction<ChartTimeRange>) => {
      state.chartTimeRange = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearData: (state) => {
      state.stats = null;
      state.chartData = [];
      state.upcomingAppointments = [];
      state.lastUpdated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch home data
      .addCase(fetchHomeData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHomeData.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.stats;
        state.chartData = action.payload.chartData;
        state.upcomingAppointments = action.payload.upcomingAppointments;
        state.lastUpdated = moment().toISOString();
        state.error = null;
      })
      .addCase(fetchHomeData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch stats
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.stats = action.payload;
        state.lastUpdated = moment().toISOString();
      })

      // Fetch chart data
      .addCase(fetchChartData.fulfilled, (state, action) => {
        state.chartData = action.payload;
        state.lastUpdated = moment().toISOString();
      })

      // Fetch upcoming appointments
      .addCase(fetchUpcomingAppointments.fulfilled, (state, action) => {
        state.upcomingAppointments = action.payload;
        state.lastUpdated = moment().toISOString();
      });
  },
});

export const { setChartTimeRange, clearError, clearData } = homeSlice.actions;
export default homeSlice.reducer;

